'use client';

import Link from 'next/link';
import { cn } from '@/lib/utils';
import { Button as DsButton } from '@/components/ds';

type PrimaryAction = {
  label: string;
  href?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  variant?: 'primary' | 'secondary';
};

type PageHeroProps = {
  title: string;
  subtitle?: string | React.ReactNode;
  icon?: React.ReactNode;
  eyebrow?: React.ReactNode;
  titleGradient?: string; // full Tailwind gradient classes e.g. "from-blue-300 to-indigo-400"
  primaryAction?: PrimaryAction;
  secondaryContent?: React.ReactNode;
  variant?: 'strip' | 'inline';
  background?: 'gradient-blue-purple' | 'slate-blur' | 'none';
  children?: React.ReactNode;
};

const BG_CLASSES: Record<NonNullable<PageHeroProps['background']>, string> = {
  // Prop kept for backwards compatibility; renders as default strip surface now.
  'gradient-blue-purple': 'bg-surface-1 border-b border-border-subtle',
  'slate-blur': 'bg-surface-2 backdrop-blur-sm border-b border-border-subtle',
  none: '',
};

export default function PageHero({
  title,
  subtitle,
  icon,
  eyebrow,
  titleGradient,
  primaryAction,
  secondaryContent,
  variant = 'strip',
  background = 'gradient-blue-purple',
  children,
}: PageHeroProps) {
  const isStrip = variant === 'strip';
  const bgClass = BG_CLASSES[background];
  const paddingClass = isStrip ? 'py-6 sm:py-8 md:py-10' : 'py-4 sm:py-6';

  const titleEl = titleGradient ? (
    <h1
      className={cn(
        'text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r bg-clip-text text-transparent',
        titleGradient
      )}
    >
      {title}
    </h1>
  ) : (
    <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-text-primary">{title}</h1>
  );

  const actionEl = primaryAction ? (
    primaryAction.href ? (
      <Link href={primaryAction.href} className="self-start sm:self-auto">
        <DsButton
          variant={primaryAction.variant === 'secondary' ? 'secondary' : 'primary'}
          size="md"
        >
          {primaryAction.icon}
          {primaryAction.label}
        </DsButton>
      </Link>
    ) : (
      <DsButton
        variant={primaryAction.variant === 'secondary' ? 'secondary' : 'primary'}
        size="md"
        onClick={primaryAction.onClick}
        className="self-start sm:self-auto"
      >
        {primaryAction.icon}
        {primaryAction.label}
      </DsButton>
    )
  ) : null;

  const inner = (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {eyebrow && <div className="mb-2">{eyebrow}</div>}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          {icon && (
            <span className="flex-shrink-0 h-6 w-6 sm:h-7 sm:w-7 text-text-secondary mt-1">
              {icon}
            </span>
          )}
          <div className="min-w-0">
            {titleEl}
            {subtitle && (
              <p className="mt-1 sm:mt-2 text-xs sm:text-sm max-w-2xl text-text-secondary">{subtitle}</p>
            )}
          </div>
        </div>
        {(actionEl || secondaryContent) && (
          <div className="flex-shrink-0 flex items-center gap-3">
            {secondaryContent}
            {actionEl}
          </div>
        )}
      </div>
      {children && <div className="mt-4">{children}</div>}
    </div>
  );

  if (bgClass) {
    return (
      <div className={cn(bgClass, paddingClass, isStrip ? 'mb-8' : 'mb-4')}>{inner}</div>
    );
  }

  return <div className={cn(paddingClass, isStrip ? 'mb-8' : 'mb-4')}>{inner}</div>;
}
